'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface Question {
  id: string;
  text: string;
  answers: {
    id: string;
    text: string;
    description?: string;
    nextQuestion?: string;
    trackResult?: 'collector' | 'debtor';
  }[];
}

const questions: Question[] = [
  {
    id: 'q1',
    text: 'Do you have a judgment against you, or are you trying to collect on a judgment?',
    answers: [
      {
        id: 'a1_debtor',
        text: 'I have a judgment against me',
        description: 'You need debtor resources',
        nextQuestion: 'q2_debtor',
        trackResult: 'debtor',
      },
      {
        id: 'a1_collector',
        text: "I'm trying to collect on a judgment",
        description: 'You need collector resources',
        nextQuestion: 'q2_collector',
        trackResult: 'collector',
      },
    ],
  },
  {
    id: 'q2_collector',
    text: "What's your primary goal?",
    answers: [
      { id: 'a2_c1', text: 'Collect full amount', nextQuestion: 'q3' },
      { id: 'a2_c2', text: 'Settle for less to close quickly', nextQuestion: 'q3' },
      { id: 'a2_c3', text: 'Structure a payment plan', nextQuestion: 'q3' },
    ],
  },
  {
    id: 'q2_debtor',
    text: "What's your primary concern?",
    answers: [
      { id: 'a2_d1', text: 'Stop wage garnishment', nextQuestion: 'q3' },
      { id: 'a2_d2', text: 'Negotiate a settlement', nextQuestion: 'q3' },
      { id: 'a2_d3', text: 'Set up a payment plan I can afford', nextQuestion: 'q3' },
    ],
  },
  {
    id: 'q3',
    text: 'Do you have documentation organized?',
    answers: [
      { id: 'a3_1', text: 'Yes, I have everything', nextQuestion: 'q4' },
      { id: 'a3_2', text: 'Partially organized', nextQuestion: 'q4' },
      { id: 'a3_3', text: 'Not at all', nextQuestion: 'q4' },
    ],
  },
  {
    id: 'q4',
    text: 'How urgent is your situation?',
    answers: [
      { id: 'a4_1', text: 'Extremely urgent (enforcement happening now)', nextQuestion: 'q5' },
      { id: 'a4_2', text: 'Moderately urgent (enforcement likely soon)', nextQuestion: 'q5' },
      { id: 'a4_3', text: 'Not urgent (planning ahead)', nextQuestion: 'q5' },
    ],
  },
  {
    id: 'q5',
    text: 'Have you attempted communication?',
    answers: [
      { id: 'a5_1', text: 'Yes, no response', nextQuestion: 'q6' },
      { id: 'a5_2', text: 'Yes, some communication', nextQuestion: 'q6' },
      { id: 'a5_3', text: 'Not yet', nextQuestion: 'q6' },
    ],
  },
  {
    id: 'q6',
    text: "What's your biggest challenge?",
    answers: [
      { id: 'a6_1', text: "Don't know what steps to take", nextQuestion: 'results' },
      { id: 'a6_2', text: 'Need templates/scripts', nextQuestion: 'results' },
      { id: 'a6_3', text: 'Need help calculating options', nextQuestion: 'results' },
    ],
  },
];

interface QuizInterfaceProps {
  onComplete: (track: 'collector' | 'debtor') => void;
}

export default function QuizInterface({ onComplete }: QuizInterfaceProps) {
  const [currentQuestionId, setCurrentQuestionId] = useState<string>('q1');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [track, setTrack] = useState<'collector' | 'debtor' | null>(null);
  const [history, setHistory] = useState<string[]>(['q1']);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  const currentQuestion = questions.find((q) => q.id === currentQuestionId);
  const progress = (Object.keys(answers).length / 6) * 100;

  const saveQuizResponse = async (finalAnswers: Record<string, string>, finalTrack: 'collector' | 'debtor') => {
    if (!user) {
      // User not logged in, skip saving
      return;
    }

    try {
      setSaving(true);
      const supabase = createClient();

      // Save quiz response to database
      const { error } = await supabase
        .from('quiz_responses')
        .insert({
          user_id: user.id,
          track: finalTrack,
          answers_json: finalAnswers,
        });

      if (error) {
        console.error('Failed to save quiz response:', error.message);
      }
    } catch (error: any) {
      console.error('Error saving quiz response:', error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAnswer = async (answerId: string) => {
    const answer = currentQuestion?.answers.find((a) => a.id === answerId);
    if (!answer) return;

    // Save answer
    const updatedAnswers = { ...answers, [currentQuestionId]: answerId };
    setAnswers(updatedAnswers);

    // Save track if first question
    let finalTrack = track;
    if (answer.trackResult) {
      setTrack(answer.trackResult);
      finalTrack = answer.trackResult;
    }

    // Navigate to next question or results
    if (answer.nextQuestion === 'results') {
      // Save quiz response before completing
      await saveQuizResponse(updatedAnswers, finalTrack || 'collector');
      onComplete(finalTrack || 'collector');
    } else if (answer.nextQuestion) {
      setCurrentQuestionId(answer.nextQuestion);
      setHistory([...history, answer.nextQuestion]);
    }
  };

  const handleBack = () => {
    if (history.length > 1) {
      const newHistory = [...history];
      newHistory.pop();
      const previousQuestionId = newHistory[newHistory.length - 1];
      setCurrentQuestionId(previousQuestionId);
      setHistory(newHistory);

      // Remove last answer
      const newAnswers = { ...answers };
      delete newAnswers[currentQuestionId];
      setAnswers(newAnswers);
    }
  };

  if (!currentQuestion) {
    return null;
  }

  return (
    <div data-component-id="src/app/start-here/components/QuizInterface.tsx:187:4" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="187" data-component-end-line="247" data-component-file="QuizInterface.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-3xl%20mx-auto%22%7D" className="max-w-3xl mx-auto">
      {/* Progress Bar */}
      <div data-component-id="src/app/start-here/components/QuizInterface.tsx:189:6" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="189" data-component-end-line="202" data-component-file="QuizInterface.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%22%7D" className="mb-12">
        <div data-component-id="src/app/start-here/components/QuizInterface.tsx:190:8" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="190" data-component-end-line="195" data-component-file="QuizInterface.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-4%22%7D" className="flex items-center justify-between mb-4">
          <span data-component-id="src/app/start-here/components/QuizInterface.tsx:191:10" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="191" data-component-end-line="193" data-component-file="QuizInterface.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-bold%20text-muted-foreground%22%2C%22textContent%22%3A%22Question%20of%206%22%7D" className="text-sm font-bold text-muted-foreground">
            Question {Object.keys(answers).length + 1} of 6
          </span>
          <span data-component-id="src/app/start-here/components/QuizInterface.tsx:194:10" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="194" data-component-end-line="194" data-component-file="QuizInterface.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-bold%20text-primary%22%2C%22textContent%22%3A%22%25%20Complete%22%7D" className="text-sm font-bold text-primary">{Math.round(progress)}% Complete</span>
        </div>
        <div data-component-id="src/app/start-here/components/QuizInterface.tsx:196:8" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="196" data-component-end-line="201" data-component-file="QuizInterface.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20h-2%20bg-muted%20rounded-full%20overflow-hidden%22%7D" className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div data-component-id="src/app/start-here/components/QuizInterface.tsx:197:10" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="197" data-component-end-line="200" data-component-file="QuizInterface.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-full%20bg-primary%20rounded-full%20transition-all%20duration-300%22%7D"
            className="h-full bg-primary rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Question */}
      <div data-component-id="src/app/start-here/components/QuizInterface.tsx:205:6" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="205" data-component-end-line="235" data-component-file="QuizInterface.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22glass%20rounded-3xl%20p-8%20md%3Ap-12%20border%20border-border%20shadow-xl%20mb-8%22%7D" className="glass rounded-3xl p-8 md:p-12 border border-border shadow-xl mb-8">
        <h2 data-component-id="src/app/start-here/components/QuizInterface.tsx:206:8" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="206" data-component-end-line="206" data-component-file="QuizInterface.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20md%3Atext-4xl%20font-bold%20mb-8%20text-foreground%22%7D" className="text-2xl md:text-4xl font-bold mb-8 text-foreground">{currentQuestion.text}</h2>

        {/* Answer Options */}
        <div data-component-id="src/app/start-here/components/QuizInterface.tsx:209:8" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="209" data-component-end-line="234" data-component-file="QuizInterface.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
          {currentQuestion.answers.map((answer) => (
            <button data-component-id="src/app/start-here/components/QuizInterface.tsx:211:12" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="211" data-component-end-line="232" data-component-file="QuizInterface.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20glass%20border%20border-border%20rounded-xl%20p-6%20hover%3Abg-card%20hover%3Aborder-primary%20transition-all%20text-left%20group%22%7D"
              key={answer.id}
              onClick={() => handleAnswer(answer.id)}
              className="w-full glass border border-border rounded-xl p-6 hover:bg-card hover:border-primary transition-all text-left group"
            >
              <div data-component-id="src/app/start-here/components/QuizInterface.tsx:216:14" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="216" data-component-end-line="231" data-component-file="QuizInterface.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
                <div data-component-id="src/app/start-here/components/QuizInterface.tsx:217:16" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="217" data-component-end-line="224" data-component-file="QuizInterface.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                  <span data-component-id="src/app/start-here/components/QuizInterface.tsx:218:18" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="218" data-component-end-line="220" data-component-file="QuizInterface.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-foreground%20group-hover%3Atext-primary%20transition-colors%22%7D" className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    {answer.text}
                  </span>
                  {answer.description && (
                    <span data-component-id="src/app/start-here/components/QuizInterface.tsx:222:20" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="222" data-component-end-line="222" data-component-file="QuizInterface.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20block%20mt-2%22%7D" className="text-sm text-muted-foreground block mt-2">{answer.description}</span>
                  )}
                </div>
                <Icon data-component-id="src/app/start-here/components/QuizInterface.tsx:225:16" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="225" data-component-end-line="230" data-component-file="QuizInterface.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowRightIcon%22%2C%22className%22%3A%22text-muted-foreground%20group-hover%3Atext-primary%20group-hover%3Atranslate-x-1%20transition-all%22%7D"
                  name="ArrowRightIcon"
                  size={20}
                  variant="outline"
                  className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all"
                />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Back Button */}
      {history.length > 1 && (
        <button data-component-id="src/app/start-here/components/QuizInterface.tsx:239:8" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="239" data-component-end-line="245" data-component-file="QuizInterface.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20text-muted-foreground%20hover%3Atext-foreground%20transition-colors%20font-medium%22%2C%22textContent%22%3A%22Back%20to%20previous%20question%22%7D"
          onClick={handleBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors font-medium"
        >
          <Icon data-component-id="src/app/start-here/components/QuizInterface.tsx:243:10" data-component-path="src/app/start-here/components/QuizInterface.tsx" data-component-line="243" data-component-end-line="243" data-component-file="QuizInterface.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowLeftIcon%22%7D" name="ArrowLeftIcon" size={20} variant="outline" />
          Back to previous question
        </button>
      )}
    </div>
  );
}